/**
 * Export Service
 * 
 * Serviço para exportação de lançamentos com dados relacionados
 */

import { supabase } from '../../../lib/supabase';
import type { CashFlowEntry } from '../../../types/database';
import type {
  ExportData,
  ExportFilters,
  ExportedTransaction,
  ExportMasterData,
} from '../../../types/export-import';

/**
 * Gera hash único para um lançamento (para detecção de duplicatas)
 * Usa Web Crypto API nativa do browser
 */
/**
 * Generate transaction hash for duplicate detection
 * 
 * Considers multiple fields to accurately identify duplicates:
 * - Company, description, amount (basic fields)
 * - Entry date, due date, payment date (temporal fields)
 * - Category, subcategory, cost center (categorization)
 * - Payment method, business partner (relationships)
 * - Invoice number (document reference)
 */
export async function generateTransactionHash(transaction: {
  company_id: string | null;
  description: string;
  amount: number;
  entry_date: string | null;
  due_date?: string | null;
  payment_date?: string | null;
  invoice_number: string | null;
  category?: string | null;
  subcategory?: string | null;
  cost_center?: string | null;
  payment_method?: string | null;
  business_partner?: string | null;
}): Promise<string> {
  const hashInput = [
    transaction.company_id || '',
    transaction.description.toLowerCase().trim(),
    transaction.amount.toFixed(2),
    transaction.entry_date || '',
    transaction.due_date || '',
    transaction.payment_date || '',
    transaction.invoice_number || '',
    transaction.category || '',
    transaction.subcategory || '',
    transaction.cost_center || '',
    transaction.payment_method || '',
    transaction.business_partner || '',
  ].join('|');

  // Usar Web Crypto API
  const encoder = new TextEncoder();
  const data = encoder.encode(hashInput);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Busca lançamentos com filtros aplicados
 * Implementa paginação para garantir que TODOS os registros sejam retornados
 */
async function fetchTransactionsForExport(
  companyId: string,
  filters: ExportFilters
): Promise<CashFlowEntry[]> {
  const allTransactions: CashFlowEntry[] = [];
  let hasMore = true;
  let page = 0;
  const pageSize = 1000; // Supabase max per request

  console.log('[ExportService] Fetching all transactions with pagination...');

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

  let query = supabase
    .from('cash_flow_entries')
    .select('*')
    .eq('company_id', companyId)
    .gte('entry_date', filters.start_date)
    .lte('entry_date', filters.end_date)
      .order('entry_date', { ascending: true })
      .range(from, to);

  // Filtrar por tipo
  if (filters.entry_type && filters.entry_type !== 'all') {
    query = query.eq('entry_type', filters.entry_type);
  }

  // Filtrar por status de pagamento
  if (filters.payment_status && filters.payment_status !== 'all') {
    query = query.eq('payment_status', filters.payment_status);
  }

  // Incluir/excluir simulações
  if (!filters.include_simulations) {
    query = query.eq('is_simulation', false);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[ExportService] Error fetching transactions:', error);
    throw new Error(`Erro ao buscar lançamentos: ${error.message}`);
  }

    if (data && data.length > 0) {
      allTransactions.push(...data);
      console.log(`[ExportService] Page ${page + 1}: ${data.length} records (total: ${allTransactions.length})`);
      
      // Check if there are more records
      hasMore = data.length === pageSize;
      page++;
    } else {
      hasMore = false;
    }
  }

  console.log(`[ExportService] Total fetched: ${allTransactions.length} transactions`);
  return allTransactions;
}

/**
 * Extrai dados mestres únicos dos lançamentos
 */
async function extractMasterData(
  transactions: CashFlowEntry[],
  companyId: string
): Promise<ExportMasterData> {
  // Coletar nomes únicos
  const categoryNames = [...new Set(transactions.map((t) => t.category).filter(Boolean))];
  const subcategoryNames = [...new Set(transactions.map((t) => t.subcategory).filter(Boolean))];
  const paymentMethodNames = [...new Set(transactions.map((t) => t.payment_method).filter(Boolean))];
  const businessPartnerNames = [...new Set(transactions.map((t) => t.business_partner).filter(Boolean))];
  const documentSourceNames = [...new Set(transactions.map((t) => t.document_source).filter(Boolean))];
  const costCenterNames = [...new Set(transactions.map((t) => t.cost_center).filter(Boolean))];
  const allTags = transactions.flatMap((t) => t.tags || []);
  const uniqueTags = [...new Set(allTags)];

  // Buscar dados completos das categorias
  const { data: categories } = await supabase
    .from('categories')
    .select('name, type, description')
    .eq('company_id', companyId)
    .in('name', categoryNames);

  // Buscar subcategorias
  const { data: subcategories } = await supabase
    .from('subcategories')
    .select('name, description, category_id')
    .eq('company_id', companyId)
    .in('name', subcategoryNames);

  // Buscar categorias para mapear subcategorias
  const categoryIds = [...new Set(subcategories?.map((s) => s.category_id).filter(Boolean) || [])];
  const { data: categoryMap } = await supabase
    .from('categories')
    .select('id, name')
    .in('id', categoryIds);

  const categoryIdToName = new Map(categoryMap?.map((c) => [c.id, c.name]) || []);

  // Buscar métodos de pagamento
  const { data: paymentMethods } = await supabase
    .from('payment_methods')
    .select('name, method_type, description')
    .eq('company_id', companyId)
    .in('name', paymentMethodNames);

  // Buscar parceiros comerciais
  const { data: businessPartners } = await supabase
    .from('business_partners')
    .select('name, type, document, email, phone')
    .eq('company_id', companyId)
    .in('name', businessPartnerNames);

  // Buscar fontes de documento
  const { data: documentSources } = await supabase
    .from('document_sources')
    .select('name, type, description')
    .eq('company_id', companyId)
    .in('name', documentSourceNames);

  // Buscar centros de custo
  const { data: costCenters } = await supabase
    .from('cost_centers')
    .select('name, code, description')
    .eq('company_id', companyId)
    .in('name', costCenterNames);

  // Buscar tags
  const { data: tags } = await supabase
    .from('tags')
    .select('name, color, description')
    .eq('company_id', companyId)
    .in('name', uniqueTags);

  return {
    categories: categories || [],
    subcategories:
      subcategories?.map((s) => ({
        name: s.name,
        category_name: categoryIdToName.get(s.category_id) || '',
        description: s.description || undefined,
      })) || [],
    payment_methods: paymentMethods || [],
    business_partners: businessPartners || [],
    document_sources: documentSources || [],
    cost_centers: costCenters || [],
    tags: tags || [],
  };
}

/**
 * Converte CashFlowEntry para ExportedTransaction
 */
async function convertToExportedTransaction(transaction: CashFlowEntry): Promise<ExportedTransaction> {
  return {
    original_id: transaction.id,
    import_hash: await generateTransactionHash({
      company_id: transaction.company_id,
      description: transaction.description,
      amount: transaction.amount,
      entry_date: transaction.entry_date,
      due_date: transaction.due_date,
      payment_date: transaction.payment_date,
      invoice_number: transaction.invoice_number,
      category: transaction.category,
      subcategory: transaction.subcategory,
      cost_center: transaction.cost_center,
      payment_method: transaction.payment_method,
      business_partner: transaction.business_partner,
    }),
    entry_type: transaction.entry_type,
    description: transaction.description,
    notes: transaction.notes,
    amount: transaction.amount,
    currency: transaction.currency,
    main_category: transaction.main_category,
    category: transaction.category,
    subcategory: transaction.subcategory,
    classification: transaction.classification,
    budget_category: transaction.budget_category,
    cost_center: transaction.cost_center,
    expense_type: transaction.expense_type,
    entry_date: transaction.entry_date,
    due_date: transaction.due_date,
    payment_date: transaction.payment_date,
    is_recurring: transaction.is_recurring,
    frequency: transaction.frequency,
    recurrence_end_date: transaction.recurrence_end_date,
    recurrence_group_id: transaction.recurrence_group_id,
    payment_status: transaction.payment_status,
    payment_method: transaction.payment_method,
    account_name: transaction.account_name,
    payment_details: transaction.payment_details,
    invoice_number: transaction.invoice_number,
    tax_category: transaction.tax_category,
    business_partner: transaction.business_partner,
    location: transaction.location,
    stakeholders: transaction.stakeholders,
    document_source: transaction.document_source,
    tags: transaction.tags,
    is_simulation: transaction.is_simulation,
    created_at: transaction.created_at,
  };
}

/**
 * Exporta lançamentos para JSON
 */
export async function exportTransactions(
  companyId: string,
  companyName: string,
  filters: ExportFilters
): Promise<ExportData> {
  console.log('[ExportService] Exporting transactions:', { companyId, filters });

  // 1. Buscar lançamentos
  const transactions = await fetchTransactionsForExport(companyId, filters);
  console.log(`[ExportService] Found ${transactions.length} transactions`);

  // 2. Extrair dados mestres
  const masterData = await extractMasterData(transactions, companyId);
  console.log('[ExportService] Master data extracted:', {
    categories: masterData.categories.length,
    payment_methods: masterData.payment_methods.length,
    business_partners: masterData.business_partners.length,
  });

  // 3. Converter transações
  const exportedTransactions = await Promise.all(
    transactions.map(transaction => convertToExportedTransaction(transaction))
  );

  // 4. Montar estrutura de exportação
  const exportData: ExportData = {
    export_metadata: {
      version: '1.0',
      exported_at: new Date().toISOString(),
      company_id: companyId,
      company_name: companyName,
      period: {
        start_date: filters.start_date,
        end_date: filters.end_date,
      },
      total_records: exportedTransactions.length,
      filters_applied: {
        entry_type: filters.entry_type,
        payment_status: filters.payment_status,
        categories: filters.categories,
      },
    },
    master_data: masterData,
    transactions: exportedTransactions,
  };

  console.log('[ExportService] Export complete');
  return exportData;
}

/**
 * Download do arquivo JSON
 */
export function downloadExportFile(data: ExportData, filename?: string): void {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download =
    filename ||
    `lancamentos_${data.export_metadata.company_id}_${data.export_metadata.period.start_date}_${data.export_metadata.period.end_date}.json`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
  
  console.log('[ExportService] File downloaded:', link.download);
}

